/**
 * Helpers for parsing `event_identifiers` entries on a session.
 *
 * Identifiers use the format `signal::additional::information`, e.g.
 * `issue::42` or `pull_request::42`. Producers may also emit provenance-only
 * identifiers that carry no display value; those are ignored here.
 */

/**
 * Extracts the value portion of the first identifier matching `prefix`.
 *
 * For an identifier like `issue::42` and prefix `issue`, this returns `42`.
 * Returns `undefined` when no matching identifier exists or the value is empty.
 */
export function findEventIdentifierValue(identifiers: string[] | null | undefined, prefix: string): string | undefined {
  const match = identifiers?.find(id => id.startsWith(`${prefix}::`))
  if (!match) return undefined

  const value = match.slice(`${prefix}::`.length)
  return value.length > 0 ? value : undefined
}

/**
 * Parses the pull request number from a session's `event_identifiers`.
 *
 * The canonical contract is `pull_request::<number>`, where `<number>` is the
 * human-facing PR number used for display (e.g. `#42`). Returns `undefined`
 * when the identifier is missing, malformed, or not a positive integer, so
 * callers can fall back gracefully for old or unexpected values.
 */
export function parsePullRequestNumber(identifiers: string[] | null | undefined): number | undefined {
  return parsePositiveIntegerEventIdentifier(identifiers, 'pull_request')
}

export function parseIssueNumber(identifiers: string[] | null | undefined): number | undefined {
  return parsePositiveIntegerEventIdentifier(identifiers, 'issue')
}

export function parseDiscussionNumber(identifiers: string[] | null | undefined): number | undefined {
  return parsePositiveIntegerEventIdentifier(identifiers, 'discussion')
}

function parsePositiveIntegerEventIdentifier(
  identifiers: string[] | null | undefined,
  prefix: string,
): number | undefined {
  const value = findEventIdentifierValue(identifiers, prefix)
  if (value === undefined) return undefined

  if (!/^\d+$/.test(value)) return undefined

  const parsed = Number.parseInt(value, 10)
  return parsed > 0 ? parsed : undefined
}
